"use client";

import type { OrganizationIcon } from "@mlx/contracts";
import { orgAvatarColors } from "@/lib/deployment-branding";
import { cn } from "@/lib/utils";

const DEFAULT_BACKGROUND = "#f97316";
const DEFAULT_TEXT = "#ffffff";

export const ORGANIZATION_PROFILE_UPDATED_EVENT =
  "mlx:organization-profile-updated";

export function resolvedOrganizationIcon(input: {
  icon?: OrganizationIcon;
  imageUrl?: string;
}): OrganizationIcon {
  if (input.icon) return input.icon;
  const deploymentColors = orgAvatarColors();
  return {
    style: input.imageUrl ? "logo" : "letter",
    backgroundColor: deploymentColors?.background ?? DEFAULT_BACKGROUND,
    textColor: deploymentColors?.text ?? DEFAULT_TEXT,
    letterScale: 0.75,
  };
}

export function OrganizationAvatar(props: {
  name: string;
  imageUrl?: string | null;
  icon?: OrganizationIcon;
  size: number;
  className?: string;
  imageAlt?: string;
}) {
  const icon = resolvedOrganizationIcon({
    icon: props.icon,
    imageUrl: props.imageUrl ?? undefined,
  });
  const letter = (props.name.trim()[0] ?? "O").toUpperCase();
  const fontSize = Math.round(
    props.size * Math.max(0.5, Math.min(icon.letterScale, 1)),
  );
  return (
    <span
      aria-hidden={props.imageAlt ? undefined : "true"}
      className={cn(
        "inline-grid shrink-0 place-items-center overflow-hidden rounded-[22%] font-semibold leading-none",
        props.className,
      )}
      style={{
        width: props.size,
        height: props.size,
        backgroundColor: icon.backgroundColor,
        color: icon.textColor,
        fontSize,
      }}
    >
      {icon.style === "logo" && props.imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={props.imageUrl}
          alt={props.imageAlt ?? ""}
          className="size-full object-cover"
        />
      ) : (
        letter
      )}
    </span>
  );
}

export function organizationIconsEqual(
  left: OrganizationIcon,
  right?: OrganizationIcon,
): boolean {
  if (!right) return false;
  return (
    left.style === right.style &&
    left.backgroundColor.toLowerCase() ===
      right.backgroundColor.toLowerCase() &&
    left.textColor.toLowerCase() === right.textColor.toLowerCase() &&
    left.letterScale === right.letterScale
  );
}
